import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Minus, Plus } from "lucide-react";
import { FLAVORS, PRICE, COMPARE_PRICE, formatPrice } from "../data/products";
import { useStore } from "../context/StoreContext";

const Product = () => {
  const { t, flavor, switchFlavor, addToCart } = useStore();
  const [qty, setQty] = useState(1);
  const active = FLAVORS[flavor];

  return (
    <section id="product" className="mx-auto max-w-6xl scroll-mt-28 px-4 py-16 sm:px-6 lg:py-24" data-testid="product-section">
      <div className="mb-8 lg:mb-12">
        <h2 className="font-display text-4xl sm:text-5xl" data-testid="product-title">{t.product.title}</h2>
        <p className="mt-2 max-w-xl text-base text-[var(--muted)]">{t.product.sub}</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
        <motion.div
          key={flavor}
          initial={{ opacity: 0.4 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="card overflow-hidden"
        >
          <img src={active.image} alt={`ZENKAI ${active.name}`} className="aspect-square w-full object-cover" data-testid="product-image" />
        </motion.div>

        <div>
          <div className="grid grid-cols-2 gap-3" data-testid="flavor-toggle">
            {Object.values(FLAVORS).map((f) => (
              <button
                key={f.id}
                data-testid={`flavor-${f.id}`}
                onClick={() => switchFlavor(f.id)}
                aria-pressed={flavor === f.id}
                className={`card p-3 text-left transition-colors ${flavor === f.id ? "border-[var(--ink)] ring-1 ring-[var(--ink)]" : "hover:border-[var(--muted)]"}`}
              >
                <img src={f.image} alt="" className="mb-2 aspect-square w-full object-cover" />
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: f.theme.accent }} />
                  <span className="font-display text-xl">{f.name}</span>
                </div>
                <p className="text-xs text-[var(--muted)]">{t.product.flavorNote[f.id]}</p>
              </button>
            ))}
          </div>

          <div className="card mt-5 p-5 sm:p-6">
            <p className="font-display text-2xl" data-testid="selected-flavor">ZENKAI · {active.name}</p>
            <div className="mt-2 flex flex-wrap items-baseline gap-3">
              <span className="font-display text-4xl" data-testid="product-price">{formatPrice(PRICE)}</span>
              <span className="text-sm text-[var(--muted)] line-through">{formatPrice(COMPARE_PRICE)}</span>
              <span className="accent-bg px-1.5 py-0.5 text-xs font-bold" style={{ color: "var(--accent-contrast)" }} data-testid="product-discount">{t.product.discount}</span>
            </div>

            <ul className="mt-5 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {t.product.facts.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check className="accent-text h-4 w-4 shrink-0" /> {f}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <div className="flex items-center justify-between border border-[var(--line)] bg-white sm:w-32" data-testid="qty-control">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3" aria-label="-"><Minus className="h-4 w-4" /></button>
                <span className="font-display text-xl" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} className="p-3" aria-label="+"><Plus className="h-4 w-4" /></button>
              </div>
              <button data-testid="buy-add-to-cart" onClick={() => addToCart(flavor, qty)} className="btn-accent flex-1 px-6 py-3.5 text-base">
                {t.product.add} — {formatPrice(PRICE * qty)}
              </button>
            </div>
            {qty < 2 && (
              <button onClick={() => setQty(2)} className="mt-3 text-xs font-semibold text-[var(--muted)] underline-offset-4 hover:underline" data-testid="buy-bundle-hint">
                {t.product.bundle}
              </button>
            )}
            <p className="mt-3 text-xs text-[var(--muted)]" data-testid="buy-shipping-note">{t.product.shipping}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Product;
